import { normalizeBusinessHoursString } from "~/lib/utils/business-hours";
import {
  buildBusinessHoursScheduleFromString,
  formatBusinessHoursScheduleForDisplay,
  formatWidgetHoursRange,
  getLocationNextOpenTime,
  getTodayBusinessHoursSlot,
  isLocationOpenNow,
  parseStoredBusinessHoursSchedule,
} from "~/lib/utils/business-hours-schedule";
import { getTimezoneFromPostalCode, inferPostalCountry } from "~/lib/utils/timezone";
import {
  pickDefaultLocation,
} from "~/lib/widget-location-lead";
/** Intake offer used when the office is closed or the lead is unavailable (no HITL marketing clause). */
export function buildHitlEnabledSentence(): string {
  return "I'd be happy to gather your information";
}

/** Remove legacy HITL marketing clause from stored/static welcome copy. */
export function stripHitlMarketingClause(message: string): string {
  return message
    .replace(
      /\s*Always a Human-in-the-Loop™ is enabled — so\s+/gi,
      " ",
    )
    .replace(/\s{2,}/g, " ")
    .replace(/\.\s+\./g, ".")
    .trim();
}

export type TimeOfDayWelcomeCta = {
  ctaQuestion: string;
};

export type TimeOfDayWelcomeInput = {
  agentName: string;
  jobTitle: string;
  organizationName: string;
  /** Default or first location from project / public locations API */
  defaultLocation?: unknown | null;
  ctaClusters?: TimeOfDayWelcomeCta[];
  appointmentsEnabled?: boolean;
  /** Lead availability from widget lead preview or collaborator status */
  leadIsAvailable?: boolean;
  fallbackMessage?: string;
};

function formatLeadFirstNameFromLocation(loc: unknown): string {
  if (!loc || typeof loc !== "object") return "our team";
  const o = loc as Record<string, unknown>;
  const spec = o.specialist;
  if (spec && typeof spec === "object") {
    const s = spec as { firstName?: string; name?: string };
    const first = s.firstName?.trim();
    if (first) return first;
    const full = s.name?.trim();
    if (full) return full.split(/\s+/)[0] ?? full;
  }
  const assignments = o.teamMemberLocations;
  if (Array.isArray(assignments)) {
    const specRow = assignments.find(
      (t: unknown) =>
        t && typeof t === "object" && (t as { isSpecialist?: boolean }).isSpecialist === true,
    ) as { teamMember?: { collaborator?: { firstName?: string }; name?: string } } | undefined;
    const tm = specRow?.teamMember;
    const first = tm?.collaborator?.firstName?.trim();
    if (first) return first;
    const name = tm?.name?.trim();
    if (name && !name.includes("@")) return name.split(/\s+/)[0] ?? name;
  }
  const fn = o.responsiblePersonFirstName;
  if (typeof fn === "string" && fn.trim()) return fn.trim();
  return "our team";
}

function formatLeadTitleFromLocation(loc: unknown): string {
  if (!loc || typeof loc !== "object") return "Lead Team Member";
  const o = loc as Record<string, unknown>;
  const spec = o.specialist;
  if (spec && typeof spec === "object") {
    const title = (spec as { title?: string }).title?.trim();
    if (title) return title;
  }
  const assignments = o.teamMemberLocations;
  if (Array.isArray(assignments)) {
    const specRow = assignments.find(
      (t: unknown) =>
        t && typeof t === "object" && (t as { isSpecialist?: boolean }).isSpecialist === true,
    ) as { teamMember?: { title?: string } } | undefined;
    const title = specRow?.teamMember?.title?.trim();
    if (title) return title;
  }
  const rpTitle = o.responsiblePersonTitle;
  if (typeof rpTitle === "string" && rpTitle.trim()) return rpTitle.trim();
  return "Lead Team Member";
}

/** Mid-sentence role label: "Patient Coordinator" → "patient coordinator". */
function lowercaseLeadTitle(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return "team member";
  return trimmed.toLowerCase();
}

/** e.g. "Monday, 9 AM" → weekday; "tomorrow at 9 AM" → relative (no "on"). */
function formatFollowUpTimingFromNextOpen(nextOpen: string | null): {
  useOn: boolean;
  when: string;
} | null {
  if (!nextOpen || nextOpen === "soon") return null;
  const trimmed = nextOpen.trim();
  if (/^tomorrow\b/i.test(trimmed)) {
    return { useOn: false, when: "tomorrow" };
  }
  if (/^at\b/i.test(trimmed) && /\btoday\b/i.test(trimmed)) {
    return { useOn: false, when: "today" };
  }
  const dayMatch = trimmed.match(/^([A-Za-z]+)/);
  const day = dayMatch?.[1];
  if (!day) return null;
  if (/^tomorrow$/i.test(day)) return { useOn: false, when: "tomorrow" };
  if (/^today$/i.test(day)) return { useOn: false, when: "today" };
  return { useOn: true, when: day };
}

/**
 * Suffix after "follow up with you" based on next open time.
 * e.g. " tomorrow", " on Monday", or "" when timing is unknown.
 */
export function formatFollowUpWhenClause(nextOpen: string | null): string {
  const timing = formatFollowUpTimingFromNextOpen(nextOpen);
  if (!timing) return "";
  return timing.useOn ? ` on ${timing.when}` : ` ${timing.when}`;
}

function buildFollowUpPhrase(leadTitle: string, nextOpen: string | null): string {
  const when = formatFollowUpWhenClause(nextOpen);
  return ` so our ${lowercaseLeadTitle(leadTitle)} can follow up with you${when}`;
}

function hitlEnabledSentence(): string {
  return buildHitlEnabledSentence();
}

function locationToOrchestratorShape(loc: unknown): {
  id?: string;
  name: string;
  zipCode?: string | null;
  city?: string | null;
  businessHours?: string | null;
  businessHoursSchedule?: unknown;
  specialist?: unknown;
  isDefaultLocation?: boolean;
} | null {
  if (!loc || typeof loc !== "object") return null;
  const o = loc as Record<string, unknown>;
  return {
    id: o.id != null ? String(o.id) : undefined,
    name: String(o.name ?? "our office"),
    zipCode: o.zipCode != null ? String(o.zipCode) : null,
    city: o.city != null ? String(o.city) : null,
    businessHours:
      o.businessHours != null ? String(o.businessHours) : null,
    businessHoursSchedule: o.businessHoursSchedule,
    specialist: o.specialist,
    isDefaultLocation: o.isDefaultLocation === true,
  };
}

function buildOfficeStatusSentence(
  input: Pick<TimeOfDayWelcomeInput, "defaultLocation" | "leadIsAvailable">,
): string | null {
  const status = resolveLocationOfficeStatus(input.defaultLocation);
  if (!status) return null;

  const leadFirst = formatLeadFirstNameFromLocation(input.defaultLocation);
  const reopenPhrase =
    !status.isOpen && status.nextOpen && status.nextOpen !== "soon"
      ? ` and will reopen ${status.nextOpen}`
      : "";

  const leadTitleLower = lowercaseLeadTitle(formatLeadTitleFromLocation(input.defaultLocation));

  if (status.isOpen) {
    if (input.leadIsAvailable) {
      return `Our office is currently open, and ${leadFirst} is available for live chat.`;
    }
    return `Our office is currently open but our ${leadTitleLower} is not available.`;
  }

  return `Our office is currently closed${reopenPhrase}.`;
}

export type LocationOfficeStatus = {
  locationName: string;
  timezone: string | null;
  businessHours: string | null;
  /** Per-day normalized schedule for agent prompts */
  scheduleDisplay: string | null;
  /** Today's hours for widget header, e.g. "7:00 am - 8:00 pm" */
  todayHoursRange: string | null;
  isOpen: boolean;
  nextOpen: string | null;
};

/** Authoritative open/closed for a location (same logic as widget header). */
export function resolveLocationOfficeStatus(
  location: unknown | null | undefined,
): LocationOfficeStatus | null {
  const locShape = locationToOrchestratorShape(location);
  if (!locShape) return null;

  const zipRaw = locShape.zipCode?.trim() ?? "";
  const tz =
    getTimezoneFromPostalCode(zipRaw, inferPostalCountry(zipRaw)) ?? null;
  const hoursRaw = locShape.businessHours?.trim() ?? "";
  const hoursForDisplay =
    normalizeBusinessHoursString(locShape.businessHours) ?? hoursRaw;

  if (!hoursRaw) return null;

  const storedSchedule = parseStoredBusinessHoursSchedule(
    locShape.businessHoursSchedule,
  );
  const isOpen = isLocationOpenNow({
    businessHours: hoursRaw,
    businessHoursSchedule: storedSchedule ?? locShape.businessHoursSchedule,
    timezone: tz,
  });
  const nextOpen = !isOpen
    ? getLocationNextOpenTime({
        businessHours: hoursRaw,
        businessHoursSchedule: storedSchedule ?? locShape.businessHoursSchedule,
        timezone: tz,
      })
    : null;

  const scheduleForDisplay =
    storedSchedule ??
    buildBusinessHoursScheduleFromString(hoursRaw, tz ?? null);

  const todaySlot = scheduleForDisplay
    ? getTodayBusinessHoursSlot(scheduleForDisplay, tz)
    : null;
  const todayHoursRange = todaySlot
    ? formatWidgetHoursRange(todaySlot.open, todaySlot.close)
    : null;

  return {
    locationName: locShape.name,
    timezone: tz,
    businessHours: hoursForDisplay,
    scheduleDisplay: scheduleForDisplay
      ? formatBusinessHoursScheduleForDisplay(scheduleForDisplay, {
          cityLabel: locShape.city,
        })
      : null,
    todayHoursRange,
    isOpen,
    nextOpen,
  };
}

/** Widget header line — location name + today's hours when available. */
export function formatWidgetOfficeStatusLine(
  status: LocationOfficeStatus,
): string {
  const locationLabel = status.locationName?.trim() || "office";
  const officePhrase = `Our ${locationLabel} office`;

  if (status.isOpen) {
    if (status.todayHoursRange) {
      return `${officePhrase} is currently open today from ${status.todayHoursRange}.`;
    }
    return `${officePhrase} is currently open today.`;
  }

  const next = status.nextOpen;
  if (!next || next === "soon") {
    if (status.todayHoursRange) {
      return `${officePhrase} is currently closed today (hours: ${status.todayHoursRange}).`;
    }
    return `${officePhrase} is currently closed.`;
  }
  return `${officePhrase} is currently closed and will reopen ${next}.`;
}

/** Injected into agent prompts so office hours match the widget header. */
export function formatOfficeStatusForAgentContext(
  status: LocationOfficeStatus,
): string {
  const reopenDetail =
    !status.isOpen && status.nextOpen && status.nextOpen !== "soon"
      ? ` Reopens ${status.nextOpen}.`
      : "";
  return [
    `Office status (authoritative): **${status.isOpen ? "OPEN" : "CLOSED"}** now at **${status.locationName}**`,
    status.timezone ? `(timezone: ${status.timezone})` : "",
    `per configured hours.${reopenDetail}`,
    status.scheduleDisplay
      ? `Normalized hours:\n${status.scheduleDisplay}`
      : "",
    "**Lead Team Member UNAVAILABLE ≠ office closed.** Report office open/closed only from this line.",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Office open/closed line for sidebar and previews (no greeting or help offers).
 */
export function buildChatOfficeStatusFromProject(args: {
  visualSettings?: {
    timeOfDayWelcomeMessageEnabled?: boolean | null;
    startingMessage?: string | null;
  } | null;
  existingLocations?: unknown[] | null;
  projectLocations?: unknown[] | null;
  leadIsAvailable?: boolean;
}): string | null {
  const defaultLocation = pickDefaultLocationForWelcome(
    args.existingLocations,
    args.projectLocations,
  );

  if (args.visualSettings?.timeOfDayWelcomeMessageEnabled === true) {
    return buildOfficeStatusSentence({
      defaultLocation,
      leadIsAvailable: args.leadIsAvailable,
    });
  }

  const staticMsg = args.visualSettings?.startingMessage?.trim() ?? "";
  if (!staticMsg) return null;

  const statusMatch = staticMsg.match(/Our office is currently[^.!?\n]+[.!?]/i);
  return statusMatch?.[0]?.trim() ?? null;
}

/**
 * Builds a chat welcome message from business hours and agent identity.
 */
export function buildTimeOfDayChatWelcomeMessage(
  input: TimeOfDayWelcomeInput,
): string {
  const agentName = input.agentName?.trim() || "your AI assistant";
  const jobTitle = input.jobTitle?.trim() || "Online Support Agent";
  const orgName = input.organizationName?.trim() || "our team";

  const greeting = `Hi, I'm ${agentName}, your ${jobTitle} at ${orgName}.`;
  const status = resolveLocationOfficeStatus(input.defaultLocation);

  if (!status) {
    return `${greeting} How can I help you today?`;
  }

  const leadTitle = formatLeadTitleFromLocation(input.defaultLocation);
  const leadTitleLower = lowercaseLeadTitle(leadTitle);
  const leadFirst = formatLeadFirstNameFromLocation(input.defaultLocation);
  const reopenPhrase =
    !status.isOpen && status.nextOpen && status.nextOpen !== "soon"
      ? ` and will reopen ${status.nextOpen}`
      : "";

  if (status.isOpen) {
    if (input.leadIsAvailable) {
      return `${greeting} Our office is currently open, and ${leadFirst} is available for live chat. How can I help you today?`;
    }

    return `${greeting} Our office is currently open but our ${leadTitleLower} is not available. ${hitlEnabledSentence()} and deliver your message so our ${leadTitleLower} can contact you directly. How can I help you today?`;
  }

  const followUpPhrase = buildFollowUpPhrase(leadTitle, status.nextOpen);

  return `${greeting} Our office is currently closed${reopenPhrase}. ${hitlEnabledSentence()}${followUpPhrase}. How can I help you today?`;
}

/**
 * Shorter copy for the closed launcher bubble (w.js) — no reopen time, HITL line, or closing question.
 */
export function buildLauncherBubbleWelcomeMessage(
  input: TimeOfDayWelcomeInput,
): string {
  const agentName = input.agentName?.trim() || "your AI assistant";
  const jobTitle = input.jobTitle?.trim() || "Online Support Agent";
  const orgName = input.organizationName?.trim() || "our team";

  const greeting = `Hi, I'm ${agentName}, your ${jobTitle} at ${orgName}.`;
  const status = resolveLocationOfficeStatus(input.defaultLocation);

  if (!status) {
    return `${greeting} How can I help you today?`;
  }

  const leadTitle = formatLeadTitleFromLocation(input.defaultLocation);
  const leadTitleLower = lowercaseLeadTitle(leadTitle);
  const leadFirst = formatLeadFirstNameFromLocation(input.defaultLocation);

  if (status.isOpen) {
    if (input.leadIsAvailable) {
      return `${greeting} Our office is currently open, and ${leadFirst} is available for live chat.`;
    }
    return `${greeting} Our office is currently open. I'd be happy to gather your information so our ${leadTitleLower} can contact you directly.`;
  }

  const followUpPhrase = buildFollowUpPhrase(leadTitle, status.nextOpen);

  return `${greeting} Our office is currently closed. I'd be happy to gather your information${followUpPhrase}.`;
}

const LAUNCHER_WELCOME_CLOSING_RE =
  /\s*(How can I help you today\?|How can I assist you today\?|What can I help you with today\?|Hey, how can I assist you today\?)\s*$/i;

/** Trim static starting messages for the compact launcher bubble. */
export function truncateStaticWelcomeForLauncher(message: string): string {
  let text = stripCtaQuickLinesFromWelcomeMessage(message.trim());
  text = text.replace(LAUNCHER_WELCOME_CLOSING_RE, "");
  text = text.replace(
    /\s*Always a Human-in-the-Loop™ is enabled — so I'd be happy to gather your information/i,
    ". I'd be happy to gather your information",
  );
  text = text.replace(/\.\s*Our office is currently closed and will reopen[^.]*\./i, ". Our office is currently closed.");
  text = text.replace(/\s{2,}/g, " ");
  return text.replace(/\.{2,}/g, ".").trim();
}

export function resolveLauncherBubbleWelcomeMessage(
  input: ResolveChatWelcomeMessageInput,
): string {
  const saved = input.staticStartingMessage?.trim() ?? "";

  if (!input.timeOfDayWelcomeMessageEnabled) {
    return truncateStaticWelcomeForLauncher(
      saved || input.fallbackMessage?.trim() || "Hello! How can I assist you today?",
    );
  }

  const generated = buildLauncherBubbleWelcomeMessage({
    ...input,
    fallbackMessage: saved || input.fallbackMessage?.trim() || "",
  });

  if (!saved) {
    return generated;
  }

  return truncateStaticWelcomeForLauncher(
    mergeCustomWelcomeWithTimeOfDay(saved, generated),
  );
}

export type ResolveChatWelcomeMessageInput = TimeOfDayWelcomeInput & {
  timeOfDayWelcomeMessageEnabled?: boolean;
  staticStartingMessage?: string | null;
};

export const OFFICE_STATUS_PLACEHOLDER = "{officeStatus}";

const CLOSING_QUESTION_RE =
  /(How can I help you today\?|How can I assist you today\?|What can I help you with today\?)\s*$/i;

/**
 * One live sentence: "Our office is currently…" or "Our Bryant Park office is currently…".
 * Must start at a sentence boundary so it does not swallow "your …" / "our live chat
 * assistant…" greetings (the old `/Our/i` matched the "our" inside "your").
 */
const OFFICE_STATUS_SENTENCE_RE =
  /(?:^|[.!?]\s+)(Our\b(?:\s+(?!office\b)[\w&.'’-]+)*\s+office is currently[^.!?]*[.!?])/i;

export function extractTimeOfDayStatusBlock(message: string): string {
  const match = message.match(OFFICE_STATUS_SENTENCE_RE);
  return match?.[1]?.replace(/\s+/g, " ").trim() ?? "";
}

function replaceOfficeStatusSentence(message: string, statusBlock: string): string {
  return message.replace(OFFICE_STATUS_SENTENCE_RE, (full, sentence: string) => {
    const prefix = full.slice(0, full.length - sentence.length);
    return `${prefix}${statusBlock} `;
  });
}

export function isTimeOfDayWelcomeTemplate(
  message: string | null | undefined,
): boolean {
  const text = message?.trim() ?? "";
  if (!text) return false;
  if (text.includes(OFFICE_STATUS_PLACEHOLDER)) return true;
  return OFFICE_STATUS_SENTENCE_RE.test(text);
}

function tidyWelcomeSpacing(message: string): string {
  return message
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.!?])/g, "$1")
    .trim();
}

/**
 * Keep the user's custom greeting/closing, and refresh the live office-hours /
 * lead-availability block from the generated Time of Day message.
 */
export function mergeCustomWelcomeWithTimeOfDay(
  customMessage: string | null | undefined,
  generatedMessage: string,
): string {
  const custom = customMessage?.trim() ?? "";
  const generated = generatedMessage.trim();
  if (!custom) return generated;

  const statusBlock = extractTimeOfDayStatusBlock(generated);
  if (!statusBlock) return custom;

  if (custom.includes(OFFICE_STATUS_PLACEHOLDER)) {
    return tidyWelcomeSpacing(
      custom.split(OFFICE_STATUS_PLACEHOLDER).join(statusBlock),
    );
  }

  if (OFFICE_STATUS_SENTENCE_RE.test(custom)) {
    return tidyWelcomeSpacing(replaceOfficeStatusSentence(custom, statusBlock));
  }

  if (CLOSING_QUESTION_RE.test(custom)) {
    return tidyWelcomeSpacing(
      custom.replace(CLOSING_QUESTION_RE, `${statusBlock} $1`),
    );
  }

  return tidyWelcomeSpacing(`${custom} ${statusBlock}`);
}

const QUICK_REPLY_ARROW_LINE = /^→\s*(.+)$/u;

/** Remove → CTA quick-reply lines from welcome text (CTAs render as widget buttons, not in the greeting). */
export function stripCtaQuickLinesFromWelcomeMessage(message: string): string {
  return message
    .split(/\r?\n/)
    .filter((line) => !QUICK_REPLY_ARROW_LINE.test(line.trim()))
    .join("\n")
    .trim();
}

/** Returns dynamic welcome when enabled, otherwise the saved static message. */
export function resolveChatWelcomeMessage(
  input: ResolveChatWelcomeMessageInput,
): string {
  const saved = input.staticStartingMessage?.trim() ?? "";
  const fallback =
    saved ||
    input.fallbackMessage?.trim() ||
    "Hello! How can I assist you today?";

  if (!input.timeOfDayWelcomeMessageEnabled) {
    return stripHitlMarketingClause(fallback);
  }

  const generated = buildTimeOfDayChatWelcomeMessage({
    ...input,
    fallbackMessage: fallback,
  });

  if (!saved) {
    return stripHitlMarketingClause(generated);
  }

  return stripHitlMarketingClause(
    mergeCustomWelcomeWithTimeOfDay(saved, generated),
  );
}

/** Resolve default location from widget/API location lists. */
export function pickDefaultLocationForWelcome(
  existingLocations: unknown[] | null | undefined,
  projectLocations: unknown[] | null | undefined,
): unknown | null {
  const list =
    Array.isArray(existingLocations) && existingLocations.length > 0
      ? existingLocations
      : Array.isArray(projectLocations) && projectLocations.length > 0
        ? projectLocations
        : [];
  return pickDefaultLocation(list);
}

export function readChatAppointmentsEnabled(channels: unknown): boolean {
  if (!channels || typeof channels !== "object") return false;
  const c = channels as Record<string, unknown>;
  if (c.chatAppointmentsEnabled === true) return true;
  if (c.chatAppointmentsEnabled === false) return false;
  const mcpId = c.chatAppointmentsMcpServerId;
  return typeof mcpId === "string" && mcpId !== "none" && mcpId.length > 0;
}

/** Project-shaped input for widget, sidebar, and settings preview. */
export function buildChatWelcomeMessageFromProject(args: {
  visualSettings?: {
    startingMessage?: string | null;
    timeOfDayWelcomeMessageEnabled?: boolean | null;
    channels?: unknown;
  } | null;
  aiAgent?: { name?: string | null; jobTitle?: string | null } | null;
  projectTitle?: string | null;
  ctaClusters?: TimeOfDayWelcomeCta[] | null;
  existingLocations?: unknown[] | null;
  projectLocations?: unknown[] | null;
  leadIsAvailable?: boolean;
}): string {
  const vs = args.visualSettings;
  const defaultLocation = pickDefaultLocationForWelcome(
    args.existingLocations,
    args.projectLocations,
  );

  const baseMessage = resolveChatWelcomeMessage({
    timeOfDayWelcomeMessageEnabled: vs?.timeOfDayWelcomeMessageEnabled === true,
    staticStartingMessage: vs?.startingMessage,
    agentName: args.aiAgent?.name?.trim() || "your AI assistant",
    jobTitle: args.aiAgent?.jobTitle?.trim() || "Online Support Agent",
    organizationName: args.projectTitle?.trim() || "our team",
    defaultLocation,
    ctaClusters: args.ctaClusters ?? [],
    appointmentsEnabled: readChatAppointmentsEnabled(vs?.channels),
    leadIsAvailable: args.leadIsAvailable,
  });

  return stripCtaQuickLinesFromWelcomeMessage(baseMessage);
}

/** Compact welcome for w.js launcher bubble only (full chat keeps HITL + closing question). */
export function buildLauncherBubbleWelcomeMessageFromProject(args: {
  visualSettings?: {
    startingMessage?: string | null;
    timeOfDayWelcomeMessageEnabled?: boolean | null;
    channels?: unknown;
  } | null;
  aiAgent?: { name?: string | null; jobTitle?: string | null } | null;
  projectTitle?: string | null;
  ctaClusters?: TimeOfDayWelcomeCta[] | null;
  existingLocations?: unknown[] | null;
  projectLocations?: unknown[] | null;
  leadIsAvailable?: boolean;
}): string {
  const vs = args.visualSettings;
  const defaultLocation = pickDefaultLocationForWelcome(
    args.existingLocations,
    args.projectLocations,
  );

  return resolveLauncherBubbleWelcomeMessage({
    timeOfDayWelcomeMessageEnabled: vs?.timeOfDayWelcomeMessageEnabled === true,
    staticStartingMessage: vs?.startingMessage,
    agentName: args.aiAgent?.name?.trim() || "your AI assistant",
    jobTitle: args.aiAgent?.jobTitle?.trim() || "Online Support Agent",
    organizationName: args.projectTitle?.trim() || "our team",
    defaultLocation,
    ctaClusters: args.ctaClusters ?? [],
    appointmentsEnabled: readChatAppointmentsEnabled(vs?.channels),
    leadIsAvailable: args.leadIsAvailable,
  });
}
